import { useCallback, useEffect, useMemo } from 'react';

import { Form, Input, notification, Select } from 'antd';

import {
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  FORM_RULES,
  MODAL_WIDTHS,
  PLACEHOLDER_MESSAGES,
  VALIDATION_RULES,
} from '@/shared/lib/constants';
import { getParentFqn } from '@/shared/lib/format/fqn';
import { areArraysEqual, transformOwnersToSelectOptions } from '@/shared/lib/utils';
import type { JsonPatchOperation } from '@/shared/types/common';
import { BaseModal } from '@/shared/ui/BaseModal';
import { DescriptionTextArea } from '@/shared/ui/DescriptionTextArea';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { normalizeGlossaryName } from '@/entities/glossary';
import { useUserOwners } from '@/entities/owner';

import type { CreateGlossaryTermRequest } from '@/features/glossary/api/createGlossaryTermApi';
import type { GlossaryTermDetail } from '@/features/glossary/api/glossaryTermDetailApi';
import { useCreateGlossaryTerm, useUpdateGlossaryTerm } from '@/features/glossary/model';
import styles from '@/features/glossary/ui/_internal/GlossaryFormModal.module.css';

type FormValues = {
  name: string;
  displayName?: string;
  description: string;
  owners?: string[];
  reviewers?: string[];
};

type ParentInfo = {
  glossaryId: string;
  glossaryName: string;
  parentId?: string;
  parentName?: string;
  parentFqn?: string;
};

type GlossaryTermFormModalProps = {
  open: boolean;
  mode?: 'create' | 'edit';
  parentInfo?: ParentInfo;
  termData?: GlossaryTermDetail;
  termFqn?: string;
  onClose: () => void;
  onSuccess?: (parentFqn: string) => void;
};

export const GlossaryTermFormModal = ({
  open,
  mode = 'create',
  parentInfo,
  termData,
  termFqn,
  onClose,
  onSuccess,
}: GlossaryTermFormModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const createMutation = useCreateGlossaryTerm();
  const updateMutation = useUpdateGlossaryTerm(termFqn);
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const { data: usersData } = useUserOwners();

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const isEditMode = mode === 'edit';

  const modalTitle = useMemo(() => {
    if (isEditMode) {
      return `용어 수정 (${termData?.displayName || termData?.name || ''})`;
    }
    if (parentInfo?.parentName) {
      return `용어 추가 (${parentInfo.parentName})`;
    }
    return `용어 추가 (${parentInfo?.glossaryName || ''})`;
  }, [isEditMode, termData, parentInfo]);

  const userOptions = useMemo(() => transformOwnersToSelectOptions(usersData), [usersData]);

  useEffect(() => {
    if (open) {
      if (isEditMode && termData) {
        form.setFieldsValue({
          name: termData.name,
          displayName: termData.displayName || '',
          description: termData.description || '',
          owners: termData.owners?.map((o) => o.id) || [],
          reviewers: termData.reviewers?.map((r) => r.id) || [],
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, form, isEditMode, termData]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const generatePatchOperations = useCallback(
    (values: FormValues): JsonPatchOperation[] => {
      if (!termData) return [];

      const operations: JsonPatchOperation[] = [];

      if (values.displayName !== (termData.displayName || '')) {
        if (values.displayName) {
          operations.push({
            op: termData.displayName ? 'replace' : 'add',
            path: '/displayName',
            value: values.displayName,
          });
        } else if (termData.displayName) {
          operations.push({ op: 'remove', path: '/displayName' });
        }
      }

      if (values.description !== (termData.description || '')) {
        if (values.description) {
          operations.push({
            op: termData.description ? 'replace' : 'add',
            path: '/description',
            value: values.description,
          });
        }
      }

      const currentOwners = termData.owners?.map((o) => o.id) || [];
      const newOwners = values.owners || [];
      if (!areArraysEqual(currentOwners, newOwners)) {
        const ownerValues = newOwners.map((id) => ({ id, type: 'user' }));
        operations.push({
          op: currentOwners.length > 0 ? 'replace' : 'add',
          path: '/owners',
          value: ownerValues,
        });
      }

      const currentReviewers = termData.reviewers?.map((r) => r.id) || [];
      const newReviewers = values.reviewers || [];
      if (!areArraysEqual(currentReviewers, newReviewers)) {
        const reviewerValues = newReviewers.map((id) => ({ id, type: 'user' }));
        operations.push({
          op: currentReviewers.length > 0 ? 'replace' : 'add',
          path: '/reviewers',
          value: reviewerValues,
        });
      }

      return operations;
    },
    [termData]
  );

  const transformFormValuesToRequest = useCallback(
    (values: FormValues): CreateGlossaryTermRequest => {
      if (!parentInfo) {
        throw new Error('parentInfo is required for create mode');
      }

      const owners = (values.owners || []).map((id) => ({
        id,
        type: 'user',
      }));

      const reviewers = (values.reviewers || []).map((id) => ({
        id,
        type: 'user',
      }));

      const request: CreateGlossaryTermRequest = {
        glossary: parentInfo.glossaryName,
        name: normalizeGlossaryName(values.name),
        displayName: values.displayName || undefined,
        description: values.description || undefined,
        owners,
        reviewers,
        mutuallyExclusive: false,
        style: {},
      };

      if (parentInfo.parentFqn) {
        request.parent = parentInfo.parentFqn;
      }

      return request;
    },
    [parentInfo]
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        if (isEditMode && termData) {
          const operations = generatePatchOperations(values);
          if (operations.length === 0) {
            notification.info({
              message: '변경 사항 없음',
              description: '수정된 내용이 없습니다.',
              placement: 'bottomLeft',
              duration: 3,
            });
            handleClose();
            return;
          }
          await updateMutation.mutateAsync({
            termId: termData.id,
            operations,
          });
          handleClose();

          if (termFqn) {
            onSuccess?.(getParentFqn(termFqn, true));
          }
        } else {
          const request = transformFormValuesToRequest(values);
          await createMutation.mutateAsync(request);
          handleClose();

          const refreshFqn = parentInfo?.parentFqn || parentInfo?.glossaryName || '';
          if (refreshFqn) {
            onSuccess?.(refreshFqn);
          }
        }
      } catch (error) {
        console.error('Failed to submit glossary term form:', error);
      }
    },
    [
      isEditMode,
      termData,
      generatePatchOperations,
      updateMutation,
      termFqn,
      transformFormValuesToRequest,
      createMutation,
      handleClose,
      onSuccess,
      parentInfo?.parentFqn,
      parentInfo?.glossaryName,
    ]
  );

  return (
    <BaseModal
      title={modalTitle}
      open={open}
      onClose={handleClose}
      width={MODAL_WIDTHS.STANDARD_FORM}
      onOk={() => form.submit()}
      okText={isEditMode ? '저장' : '생성'}
      confirmLoading={isLoading}>
      <Form form={form} layout="vertical" onFinish={handleSubmit} className={styles.form}>
        <Form.Item
          name="name"
          label={FORM_LABELS.NAME}
          rules={[...FORM_RULES.REQUIRED_NAME, ...FORM_RULES.GLOSSARY_TERM_NAME]}
          validateFirst>
          <Input
            placeholder={FORM_PLACEHOLDERS.NAME}
            disabled={isEditMode}
            allowClear
            maxLength={VALIDATION_RULES.PROJECT_NAME_MAX_LENGTH}
          />
        </Form.Item>

        <Form.Item name="displayName" label={FORM_LABELS.DISPLAY_NAME}>
          <Input
            placeholder={FORM_PLACEHOLDERS.DISPLAY_NAME}
            maxLength={VALIDATION_RULES.DISPLAY_NAME_MAX_LENGTH}
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={FORM_LABELS.DESCRIPTION}
          rules={[...FORM_RULES.REQUIRED_DESCRIPTION, ...FORM_RULES.GLOSSARY_TERM_DESCRIPTION]}
          validateTrigger={['onSubmit']}
          validateFirst>
          <DescriptionTextArea
            placeholder={FORM_PLACEHOLDERS.DESCRIPTION}
            showHelper
            maxLength={VALIDATION_RULES.GLOSSARY_DESCRIPTION_MAX_LENGTH}
          />
        </Form.Item>

        {showOwners && (
          <Form.Item name="owners" label={FORM_LABELS.OWNERS}>
            <Select mode="multiple" placeholder={PLACEHOLDER_MESSAGES.OWNER_SELECT} allowClear options={userOptions} />
          </Form.Item>
        )}

        {showOwners && (
          <Form.Item name="reviewers" label={FORM_LABELS.REVIEWERS}>
            <Select mode="multiple" placeholder={PLACEHOLDER_MESSAGES.OWNER_SELECT} allowClear options={userOptions} />
          </Form.Item>
        )}
      </Form>
    </BaseModal>
  );
};
